<template>
  <section class="insurance-admin-agent-dashboard py-6 px-4">
    <b-container fluid>
      <b-card class="border-0 shadow-sm">
        <template #header>
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
            <div>
              <h2 class="h4 text-primary mb-1">Insurance admin dashboard</h2>
              <p class="text-secondary mb-0" style="color: #495057 !important;">
                Review agent performance by date. Choose any agent, or an admin agent to narrow that list.
              </p>
            </div>
            <div class="d-flex gap-2 mt-3 mt-md-0">
              <b-button
                variant="success"
                :disabled="loadingExcel || !filters.start_date || !filters.end_date"
                @click="downloadExcel"
              >
                <i class="fas fa-file-excel mr-2"></i>
                <span v-if="!loadingExcel">Excel</span>
                <b-spinner v-else small label="Loading..."></b-spinner>
              </b-button>
              <b-button
                variant="primary"
                :disabled="loading"
                @click="fetchDashboardData"
              >
                <span v-if="!loading">Search</span>
                <b-spinner v-else small label="Loading..."></b-spinner>
              </b-button>
            </div>
          </div>
        </template>

        <b-form @submit.prevent="fetchDashboardData">
          <b-row>
            <b-col cols="12" md="2" class="mb-3">
              <label class="font-weight-semibold">Start date</label>
              <flat-pickr
                v-model="filters.start_date"
                :config="dateConfig"
                class="form-control"
                placeholder="YYYY-MM-DD"
              />
            </b-col>

            <b-col cols="12" md="2" class="mb-3">
              <label class="font-weight-semibold">End date</label>
              <flat-pickr
                v-model="filters.end_date"
                :config="dateConfig"
                class="form-control"
                placeholder="YYYY-MM-DD"
              />
            </b-col>

            <b-col cols="12" md="3" class="mb-3">
              <label class="font-weight-semibold d-block">Admin agent</label>
              <treeselect
                v-model="filters.admin_user_id"
                :options="adminOptions"
                :normalizer="adminNormalizer"
                :multiple="false"
                :clearable="true"
                :disable-branch-nodes="true"
                placeholder="Select an admin"
                no-options-text="No admins available"
              />
            </b-col>

            <b-col cols="12" md="3" class="mb-3">
              <label class="font-weight-semibold d-block">Agent</label>
              <treeselect
                v-model="filters.agent_user_id"
                :options="agentOptions"
                :normalizer="agentNormalizer"
                :multiple="false"
                :clearable="true"
                :disable-branch-nodes="true"
                placeholder="Select an agent"
                :no-options-text="agentOptionsLoading ? 'Loading agents...' : 'No agents available'"
                :disabled="agentOptionsLoading"
              />
            </b-col>

            <b-col cols="12" md="3" class="mb-3">
              <label class="font-weight-semibold d-block">Insurance type</label>
              <treeselect
                v-model="filters.type_insurance"
                :options="insuranceTypeOptions"
                :normalizer="insuranceTypeNormalizer"
                :multiple="false"
                :clearable="true"
                :disable-branch-nodes="true"
                placeholder="Select an insurance type"
                no-options-text="No insurance types available"
              />
            </b-col>
          </b-row>

          <b-button variant="primary" type="submit" class="d-none">
            Search
          </b-button>
        </b-form>

        <b-alert
          variant="danger"
          :show="Boolean(errorMessage)"
          dismissible
          @dismissed="errorMessage = null"
          class="mt-3"
        >
          {{ errorMessage }}
        </b-alert>

        <div class="mt-4">
          <div v-if="loading" class="d-flex align-items-center justify-content-center">
            <SpinnerLoading />
          </div>

          <div v-else>
            <b-row class="mb-4">
              <b-col
                v-for="card in summaryCards"
                :key="card.key"
                cols="12"
                md="3"
                class="mb-3"
              >
                <div class="summary-card p-3 rounded shadow-sm h-100">
                  <p class="mb-1 font-weight-semibold" style="color: #495057 !important;">
                    {{ card.label }}
                  </p>
                  <p class="h4 text-primary mb-0">{{ card.display }}</p>
                </div>
              </b-col>
            </b-row>

            <b-card class="border-0 shadow-sm mb-4">
              <template #header>
                <div class="d-flex justify-content-between align-items-center">
                  <h3 class="h5 mb-0 text-primary">Visit distribution</h3>
                  <span class="small" style="color: #495057 !important;">
                    Total visits: {{ formatNumber(totalVisitCount, true) }}
                  </span>
                </div>
              </template>
              <div v-if="visitCountChartSeries[0].data.length">
                <apexchart
                  type="bar"
                  height="320"
                  :options="visitCountChartOptions"
                  :series="visitCountChartSeries"
                />
              </div>
              <b-alert v-else variant="light" show>
                No chart data available.
              </b-alert>
            </b-card>


            <b-card class="border-0 shadow-sm mb-4">
              <template #header>
                <h3 class="h5 mb-0 text-primary">Patients by insurance type</h3>
              </template>
              <div v-if="patientsByInsuranceChartSeries[0].data.length">
                <apexchart
                  type="bar"
                  height="360"
                  :options="patientsByInsuranceChartOptions"
                  :series="patientsByInsuranceChartSeries"
                />
              </div>
              <b-alert v-else variant="light" show>
                No insurance distribution available.
              </b-alert>
            </b-card>

            <b-card class="border-0 shadow-sm mb-4">
              <template #header>
                <div class="d-flex justify-content-between align-items-center">
                  <h3 class="h5 mb-0 text-primary">Visit totals</h3>
                  <span class="small" style="color: #495057 !important;">
                    Compare NP, NC and overall totals.
                  </span>
                </div>
              </template>
              <div v-if="hasVisitTotalsData">
                <b-row>
                  <b-col cols="12" md="7" class="mb-3 mb-md-0">
                    <apexchart
                      type="pie"
                      height="360"
                      :options="visitTotalsPieOptions"
                      :series="visitTotalsPieSeries"
                    />
                  </b-col>
                  <b-col
                    cols="12"
                    md="5"
                    class="d-flex flex-column justify-content-center"
                  >
                    <div
                      v-for="item in visitTotalsLegend"
                      :key="item.label"
                      class="visit-totals-legend-item d-flex align-items-center mb-3"
                    >
                      <span
                        class="visit-totals-legend-dot mr-3"
                        :style="{ backgroundColor: item.color }"
                      ></span>
                      <div>
                        <p class="mb-1 font-weight-semibold text-dark">
                          {{ item.label }}
                        </p>
                    <p class="mb-0 text-primary font-weight-bold" style="font-size: 1.5rem;">
                          {{ formatNumber(item.value) }}
                        </p>
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row class="mt-4">
                  <b-col
                    v-for="card in financialSummaryCards"
                    :key="card.key"
                    cols="12"
                    md="4"
                    class="mb-3"
                  >
                    <div class="summary-card p-3 rounded shadow-sm h-100">
                      <p class="mb-1 font-weight-semibold" style="color: #495057 !important;">
                        {{ card.label }}
                      </p>
                      <p class="h4 text-primary mb-0">{{ card.display }}</p>
                    </div>
                  </b-col>
                </b-row>
              </div>
              <b-alert v-else variant="light" show>
                No totals available to display.
              </b-alert>
            </b-card>

            <b-card class="border-0 shadow-sm">
              <template #header>
                <div class="d-flex justify-content-between align-items-center">
                  <h3 class="h5 mb-0 text-primary">{{ isGroupedByAgent ? 'Agents and patients' : 'Patients and visits' }}</h3>
                  <span class="small" style="color: #495057 !important;">
                    Records: {{ filteredPatients.length }}
                  </span>
                </div>
              </template>

              <b-form-group
                :label="isGroupedByAgent ? 'Search agent' : 'Search patient'"
                label-for="patient-search"
                label-class="font-weight-semibold"
              >
                <b-form-input
                  id="patient-search"
                  v-model="patientSearch"
                  :placeholder="isGroupedByAgent ? 'Agent name' : 'Name, phone, insurance or agent'"
                />
              </b-form-group>

              <b-alert v-if="isGroupedByAgent && !agentRows.length" show variant="info">
                No records found for the selected filters.
              </b-alert>
              <b-alert v-else-if="!isGroupedByAgent && !patientRows.length" show variant="info">
                No records found for the selected filters.
              </b-alert>

              <template v-else>
                <b-table
                  :fields="tableFields"
                  :items="filteredPatients"
                  responsive="sm"
                  head-variant="light"
                  class="agent-data-table"
                  show-empty
                  empty-text="Sin coincidencias."
                >
                  <template v-if="isGroupedByAgent" #cell(total_amount)="row">
                    {{ formatNumber(row.item.total_amount) }}
                  </template>
                  <template v-if="isGroupedByAgent" #cell(patients_count)="row">
                    {{ formatNumber(row.item.patients_count, true) }}
                  </template>
                  <template v-if="isGroupedByAgent" #cell(total_visits)="row">
                    {{ formatNumber(row.item.total_visits, true) }}
                  </template>
                  <template v-if="!isGroupedByAgent" #cell(agentName)="row">
                    {{ row.item.agentName || "Unknown Agent" }}
                  </template>
                  <template v-if="!isGroupedByAgent" #cell(total_visit_amount)="row">
                    {{ formatNumber(row.item.total_visit_amount) }}
                  </template>
                  <template #cell(actions)="row">
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      @click="row.toggleDetails"
                    >
                      {{ row.detailsShowing ? "Hide" : "View" }} {{ isGroupedByAgent ? "patients" : "visits" }}
                    </b-button>
                  </template>
                  <template v-if="isGroupedByAgent" #row-details="row">
                    <div class="p-3">
                      <h5 class="mb-3 text-primary">Patients for {{ row.item.agent_name }}</h5>
                      <b-table
                        :items="row.item.patients"
                        :fields="agentTableFields"
                        small
                        bordered
                        responsive="sm"
                        head-variant="light"
                        class="mb-2"
                        show-empty
                        empty-text="No patients registered."
                      >
                        <template #cell(agentName)="patientRow">
                          {{ patientRow.item.agentName || "Unknown Agent" }}
                        </template>
                        <template #cell(total_visit_amount)="patientRow">
                          {{ formatNumber(patientRow.item.total_visit_amount) }}
                        </template>
                        <template #cell(actions)="patientRow">
                          <b-button
                            size="sm"
                            variant="outline-secondary"
                            @click="patientRow.toggleDetails"
                          >
                            {{ patientRow.detailsShowing ? "Hide" : "View" }} visits
                          </b-button>
                        </template>
                        <template #row-details="patientRow">
                          <b-table
                            :items="patientRow.item.visits"
                            :fields="visitDetailFields"
                            small
                            bordered
                            responsive="sm"
                            head-variant="light"
                            class="mb-2"
                            show-empty
                            empty-text="No visits registered."
                          >
                            <template #cell(total)="visitRow">
                              {{ formatNumber(visitRow.item.total) }}
                            </template>
                          </b-table>
                        </template>
                      </b-table>
                    </div>
                  </template>
                  <template v-else #row-details="row">
                    <b-table
                      :items="row.item.visits"
                      :fields="visitDetailFields"
                      small
                      bordered
                      responsive="sm"
                      head-variant="light"
                      class="mb-2"
                      show-empty
                      empty-text="No visits registered."
                    >
                      <template #cell(total)="visitRow">
                        {{ formatNumber(visitRow.item.total) }}
                      </template>
                    </b-table>
                  </template>
                </b-table>
              </template>
            </b-card>

          </div>
        </div>
      </b-card>
    </b-container>
  </section>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BCol,
  BContainer,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BSpinner,
  BTable,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import VueApexCharts from "vue-apexcharts";
import * as XLSX from "xlsx";
import adminAgentDataService from "@/core/services/apiInsurance/adminAgentData";
import {
  agentOptionsAfterAdmin,
  agentOptionsAfterAdminFailure,
  agentOptionsAfterAll,
  agentOptionsAfterAllFailure,
} from "@/view/pages/insuranceAdminAgent/agentFilter";
import insuranceNameService from "@/core/services/apiInsurance/admin/consults/insuranceName";
import SpinnerLoading from "@/components/SpinnerLoading.vue";

export default {
  name: "InsuranceAdminAgentDashboard",
  components: {
    BAlert,
    BButton,
    BCard,
    BCol,
    BContainer,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BSpinner,
    BTable,
    flatPickr,
    apexchart: VueApexCharts,
    SpinnerLoading,
  },
  data() {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      loading: false,
      errorMessage: null,
      rawDashboardResponse: null,
      dashboardData: null,
      lastFetchedPayload: null,
      rawPatients: [],
      rawAgents: [],
      patientRows: [],
      agentRows: [],
      visitCounts: [],
      totals: {},
      patientsByInsurance: [],
      isGroupedByAgent: false,
      filters: {
        start_date: firstDayOfMonth.toISOString().slice(0, 10),
        end_date: today.toISOString().slice(0, 10),
        admin_user_id: null,
        agent_user_id: null,
        type_insurance: null,
      },
      shouldAutoFetch: false,
      adminOptions: [],
      allAgentOptions: [],
      agentOptions: [],
      agentOptionsLoading: false,
      isSettingAgentFilter: false,
      insuranceTypeOptions: [],
      patientSearch: "",
      loadingExcel: false,
      dateConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
      },
      visitCountChartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
        },
        dataLabels: {
          enabled: true,
        },
        plotOptions: {
          bar: {
            columnWidth: "45%",
            borderRadius: 4,
          },
        },
        xaxis: {
          categories: [],
          labels: {
            rotate: -45,
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => Math.round(value),
          },
        },
        colors: ["#1E88E5"],
      },
      visitCountChartSeries: [
        {
          name: "Visitas",
          data: [],
        },
      ],
      patientsByInsuranceChartOptions: {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "45%",
            borderRadius: 4,
          },
        },
        xaxis: {
          categories: [],
          labels: {
            rotate: -45,
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => Math.round(value),
          },
        },
        legend: {
          position: "top",
        },
        dataLabels: {
          enabled: true,
        },
        fill: {
          opacity: 1,
        },
        colors: ["#1E88E5", "#26C6DA"],
      },
      patientsByInsuranceChartSeries: [
        {
          name: "New patients (NP)",
          data: [],
        },
        {
          name: "Returning patients (NC)",
          data: [],
        },
      ],
      visitTotalsPieOptions: {
        chart: {
          type: "pie",
        },
        labels: [
          "Returning patients (NC)",
          "New patients (NP)",
          "Total revenue",
        ],
        colors: ["#26C6DA", "#1E88E5", "#7E57C2"],
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: true,
          formatter(val, opts) {
            const seriesVal =
              opts?.w?.globals?.series?.[opts.seriesIndex] || 0;
            return new Intl.NumberFormat("es-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
            }).format(seriesVal);
          },
        },
        tooltip: {
          y: {
            formatter: (value) =>
              new Intl.NumberFormat("es-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
              }).format(value || 0),
          },
        },
      },
      visitDetailFields: [
        { key: "visit_date", label: "Date" },
        { key: "facility", label: "Facility" },
        { key: "type_membership", label: "Membership" },
        { key: "type_insurance", label: "Insurance" },
        { key: "total", label: "Total" },
      ],
    };
  },
  computed: {
    tableFields() {
      if (this.isGroupedByAgent) {
        return [
          { key: "agent_name", label: "Agent", sortable: true },
          { key: "patients_count", label: "Patients", sortable: true },
          { key: "total_visits", label: "Total visits", sortable: true },
          { key: "total_amount", label: "Win total", sortable: true },
          { key: "actions", label: "Details", class: "text-center" },
        ];
      }
      return [
        { key: "agentName", label: "Agent", sortable: true },
        { key: "fullName", label: "Patient", sortable: true },
        { key: "phone", label: "Phone" },
        { key: "typeInsuranceLabel", label: "Insurance type" },
        { key: "date_birth", label: "Birthday" },
        { key: "visits_count", label: "Visits", sortable: true },
        { key: "total_visit_amount", label: "Win total" },
        { key: "actions", label: "Details", class: "text-center" },
      ];
    },
    agentTableFields() {
      return [
        { key: "agentName", label: "Agent", sortable: true },
        { key: "fullName", label: "Patient", sortable: true },
        { key: "phone", label: "Phone" },
        { key: "typeInsuranceLabel", label: "Insurance type" },
        { key: "date_birth", label: "Birthday" },
        { key: "visits_count", label: "Visits", sortable: true },
        { key: "total_visit_amount", label: "Win total" },
        { key: "actions", label: "Details", class: "text-center" },
      ];
    },
    filteredPatients() {
      if (this.isGroupedByAgent) {
        if (!this.patientSearch) return this.agentRows;
        const search = this.patientSearch.toLowerCase();
        return this.agentRows.filter((agent) =>
          [agent.agent_name]
            .filter(Boolean)
            .some((value) =>
              value.toString().toLowerCase().includes(search)
            )
        );
      }
      if (!this.patientSearch) return this.patientRows;
      const search = this.patientSearch.toLowerCase();
      return this.patientRows.filter((patient) =>
        [patient.fullName, patient.phone, patient.typeInsuranceLabel, patient.agentName]
          .filter(Boolean)
          .some((value) =>
            value.toString().toLowerCase().includes(search)
          )
      );
    },
    insuranceTypeMap() {
      return this.insuranceTypeOptions.reduce((map, type) => {
        map[type.id] = type.name;
        return map;
      }, {});
    },
    summaryCards() {
      const totalPatients = this.isGroupedByAgent
        ? this.agentRows.reduce((sum, agent) => sum + (agent.patients_count || 0), 0)
        : this.patientRows.length;
      return [
        {
          key: "total_patients",
          label: "Managed patients",
          value: totalPatients,
          display: this.formatNumber(totalPatients, true),
        },
        {
          key: "total_visits",
          label: "Total visits",
          value: this.totalVisitCount,
          display: this.formatNumber(this.totalVisitCount, true),
        },
      ];
    },
    selectedAdmin() {
      const adminId = Number(this.filters.admin_user_id);
      if (!adminId) return null;
      return (
        this.adminOptions.find(
          (admin) => Number(admin.id) === adminId
        ) || null
      );
    },
    totalAdminAgentValue() {
      const adminPay = Number(this.selectedAdmin?.pay_to_visit) || 0;
      if (!adminPay) return 0;
      
      // Si está agrupado por agente, usar el número de agentes que tienen datos
      if (this.isGroupedByAgent) {
        const agentCount = this.agentRows.length;
        return adminPay * agentCount;
      }
      
      // Si no está agrupado, usar el número de agentes disponibles
      const agentCount = this.agentOptions.length;
      return adminPay * agentCount;
    },
    visitsPerAgentMap() {
      if (this.isGroupedByAgent) {
        return this.agentRows.reduce((map, agent) => {
          const agentId = Number(agent.agent_id);
          if (!agentId) return map;
          const visitCount = agent.total_visits || 0;
          if (!visitCount) return map;
          map[agentId] = (map[agentId] || 0) + visitCount;
          return map;
        }, {});
      }
      return this.patientRows.reduce((map, patient) => {
        const agentId = this.getPatientAgentId(patient);
        if (!agentId) return map;
        const visitCount = Array.isArray(patient.visits)
          ? patient.visits.length
          : 0;
        if (!visitCount) return map;
        map[agentId] = (map[agentId] || 0) + visitCount;
        return map;
      }, {});
    },
    totalAgentValue() {
      if (this.isGroupedByAgent) {
        return this.agentRows.reduce((sum, agent) => {
          const agentId = Number(agent.agent_id) || null;
          if (!agentId) return sum;
          const visitCount = agent.total_visits || 0;
          const agentPay = Number(agent.agent_pay) || 0;
          return sum + agentPay * visitCount;
        }, 0);
      }
      return this.agentOptions.reduce((sum, agent) => {
        const agentId = Number(agent.id) || null;
        if (!agentId) return sum;
        const visitCount = this.visitsPerAgentMap[agentId] || 0;
        const agentPay = Number(agent.pay_to_visit) || 0;
        return sum + agentPay * visitCount;
      }, 0);
    },
    totalWinPayoutValue() {
      return this.totalAdminAgentValue + this.totalAgentValue;
    },
    netInsuranceCollectionValue() {
      return this.winTotalValue - this.totalWinPayoutValue;
    },
    financialSummaryCards() {
      return [
        {
          key: "total_admin_agent",
          label: "Total admin agent",
          value: this.totalAdminAgentValue,
          display: this.formatCurrency(this.totalAdminAgentValue),
        },
        {
          key: "total_agent",
          label: "Total agent",
          value: this.totalAgentValue,
          display: this.formatCurrency(this.totalAgentValue),
        },
        {
          key: "total_win",
          label: "Total win",
          value: this.totalWinPayoutValue,
          display: this.formatCurrency(this.totalWinPayoutValue),
        },
        // {
        //   key: "total_insurance_collection",
        //   label: "Total recolectado insurance",
        //   value: this.netInsuranceCollectionValue,
        //   display: this.formatCurrency(this.netInsuranceCollectionValue),
        // },
      ];
    },
    totalVisitCount() {
      if (this.isGroupedByAgent) {
        return this.agentRows.reduce((total, agent) => {
          return total + (agent.total_visits || 0);
        }, 0);
      }
      return this.patientRows.reduce((total, patient) => {
        const visits = Array.isArray(patient.visits) ? patient.visits : [];
        return total + visits.length;
      }, 0);
    },
    npTotalValue() {
      if (this.isGroupedByAgent) {
        return this.agentRows.reduce((sum, agent) => {
          return (
            sum +
            (Array.isArray(agent.patients) ? agent.patients : []).reduce(
              (patientSum, patient) => {
                const visits = Array.isArray(patient.visits) ? patient.visits : [];
                return (
                  patientSum +
                  visits
                    .filter((visit) => this.isMembershipNP(visit.type_membership))
                    .reduce(
                      (acc, visit) => acc + (Number(visit.total) || 0),
                      0
                    )
                );
              },
              0
            )
          );
        }, 0);
      }
      return this.patientRows.reduce((sum, patient) => {
        const visits = Array.isArray(patient.visits) ? patient.visits : [];
        return (
          sum +
          visits
            .filter((visit) => this.isMembershipNP(visit.type_membership))
            .reduce(
              (acc, visit) => acc + (Number(visit.total) || 0),
              0
            )
        );
      }, 0);
    },
    ncTotalValue() {
      if (this.isGroupedByAgent) {
        return this.agentRows.reduce((sum, agent) => {
          return (
            sum +
            (Array.isArray(agent.patients) ? agent.patients : []).reduce(
              (patientSum, patient) => {
                const visits = Array.isArray(patient.visits) ? patient.visits : [];
                return (
                  patientSum +
                  visits
                    .filter((visit) => this.isMembershipNC(visit.type_membership))
                    .reduce(
                      (acc, visit) => acc + (Number(visit.total) || 0),
                      0
                    )
                );
              },
              0
            )
          );
        }, 0);
      }
      return this.patientRows.reduce((sum, patient) => {
        const visits = Array.isArray(patient.visits) ? patient.visits : [];
        return (
          sum +
          visits
            .filter((visit) => this.isMembershipNC(visit.type_membership))
            .reduce(
              (acc, visit) => acc + (Number(visit.total) || 0),
              0
            )
        );
      }, 0);
    },
    winTotalValue() {
      return this.npTotalValue + this.ncTotalValue;
    },
    visitTotalsPieSeries() {
      return [this.ncTotalValue, this.npTotalValue, this.winTotalValue];
    },
    hasVisitTotalsData() {
      return this.visitTotalsPieSeries.some((value) => Number(value) > 0);
    },
    visitTotalsLegend() {
      const colors = this.visitTotalsPieOptions.colors || [];
      return [
        {
          label: "Returning patients (NC)",
          value: this.ncTotalValue,
          color: colors[0] || "#26C6DA",
        },
        {
          label: "New patients (NP)",
          value: this.npTotalValue,
          color: colors[1] || "#1E88E5",
        },
        {
          label: "Total revenue",
          value: this.winTotalValue,
          color: colors[2] || "#7E57C2",
        },
      ];
    },
    formattedRawResponse() {
      if (!this.rawDashboardResponse) return "No data available.";
      try {
        return JSON.stringify(this.rawDashboardResponse, null, 2);
      } catch (error) {
        return String(this.rawDashboardResponse);
      }
    },
  },
  methods: {
    getPatientAgentId(patient = {}) {
      const directCandidates = [
        patient.agent_user_id,
        patient.agent_id,
        patient.agentId,
        patient.agent_insurance_id,
        patient.agent?.id,
        patient.agent?.agent_id,
        patient.agent?.agent_user_id,
        patient.agent?.user_id,
        patient.agent?.agent_insurance_id,
        patient.agent_user?.id,
        patient.agent_user?.agent_id,
        patient.agent_user?.agent_user_id,
        patient.agent_user?.agent_insurance_id,
        patient.agent_insurance?.id,
        patient.agent_insurance?.agent_id,
        patient.agent_insurance?.agent_user_id,
        patient.agent_insurance?.agent_insurance_id,
        patient.pivot?.agent_user_id,
        patient.pivot?.agent_id,
        patient.pivot?.agent_insurance_id,
        Array.isArray(patient.agents) ? patient.agents[0]?.id : null,
      ];

      for (const candidate of directCandidates) {
        const numeric = Number(candidate);
        if (numeric) return numeric;
      }

      if (Array.isArray(patient.visits)) {
        for (const visit of patient.visits) {
          const visitCandidate =
            visit?.agent_user_id ||
            visit?.agent_id ||
            visit?.agentId ||
            visit?.agent_insurance_id;
          const numeric = Number(visitCandidate);
          if (numeric) return numeric;
        }
      }

      return null;
    },
    adminNormalizer(node) {
      const parts = [node.name, node.lastname].filter(Boolean);
      return {
        id: node.id,
        label: parts.length ? parts.join(" ") : node.email || `Admin ${node.id}`,
        isDisabled: false,
      };
    },
    agentNormalizer(node) {
      const parts = [node.name, node.lastname].filter(Boolean);
      return {
        id: node.id,
        label: parts.length ? parts.join(" ") : `Agent ${node.id}`,
        isDisabled: false,
      };
    },
    insuranceTypeNormalizer(node) {
      return {
        id: node.id,
        label: node.name || `Type ${node.id}`,
        isDisabled: false,
      };
    },
    async loadAdminOptions() {
      try {
        const response = await adminAgentDataService.getAdminUsers();
        const records =
          Array.isArray(response?.data?.data)
            ? response.data.data
            : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
            ? response
            : [];
        const options = records.map((admin) => ({
          id: admin.id,
          name: admin.name,
          lastname: admin.lastname,
          email: admin.email,
          pay_to_visit: Number(admin.pay_to_visit) || 0,
        }));
        this.adminOptions = options;
      } catch (error) {
        this.adminOptions = [];
        this.errorMessage =
          "We could not load the available admins.";
      }
    },
    async loadInsuranceTypes() {
      try {
        const response = await insuranceNameService.visitList(100);
        const types = Array.isArray(response?.data) ? response.data : [];
        this.insuranceTypeOptions = types;
        this.refreshPatientRows();
      } catch (error) {
        this.insuranceTypeOptions = [];
      }
    },
    agentFilterState() {
      return {
        adminUserId: this.filters.admin_user_id,
        agentUserId: this.filters.agent_user_id,
        allAgentOptions: this.allAgentOptions,
        agentOptions: this.agentOptions,
      };
    },
    async applyAgentFilterState(next) {
      const selectionCleared = this.filters.agent_user_id && !next.agentUserId;
      this.allAgentOptions = next.allAgentOptions;
      this.agentOptions = next.agentOptions;
      if (!selectionCleared) return;
      this.isSettingAgentFilter = true;
      this.filters.agent_user_id = null;
      await this.$nextTick();
      this.isSettingAgentFilter = false;
    },
    async loadAllAgents() {
      this.agentOptionsLoading = true;
      try {
        const response = await adminAgentDataService.getAllAgents();
        await this.applyAgentFilterState(
          agentOptionsAfterAll(this.agentFilterState(), response)
        );
      } catch (error) {
        await this.applyAgentFilterState(
          agentOptionsAfterAllFailure(this.agentFilterState())
        );
      } finally {
        if (!this.filters.admin_user_id) this.agentOptionsLoading = false;
      }
    },
    async loadAgentOptions(adminId) {
      if (!adminId) {
        await this.applyAgentFilterState(
          agentOptionsAfterAdmin(this.agentFilterState(), null)
        );
        return;
      }
      this.agentOptionsLoading = true;
      try {
        const response = await adminAgentDataService.getAgentsByAdmin(adminId);
        await this.applyAgentFilterState(
          agentOptionsAfterAdmin(this.agentFilterState(), adminId, response)
        );
      } catch (error) {
        await this.applyAgentFilterState(
          agentOptionsAfterAdminFailure(this.agentFilterState(), adminId)
        );
      } finally {
        this.agentOptionsLoading = false;
      }
    },
    buildFiltersPayload() {
      const payload = {};
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value !== null && value !== "" && value !== undefined) {
          payload[key] = value;
        }
      });
      if (payload.admin_user_id) {
        payload.admin_user_id = Number(payload.admin_user_id);
      }
      if (payload.agent_user_id) {
        payload.agent_id = Number(payload.agent_user_id);
        delete payload.agent_user_id;
      }
      if (payload.type_insurance) {
        payload.type_insurance = Number(payload.type_insurance);
      }
      return payload;
    },
    downloadExcel() {
      if (!this.filters.start_date || !this.filters.end_date) {
        this.errorMessage = "Start and end dates are required.";
        return;
      }

      const data = this.dashboardData;
      if (
        !data ||
        this.lastFetchedPayload !== JSON.stringify(this.buildFiltersPayload())
      ) {
        this.errorMessage =
          "Please click Search to load the data for the selected filters before exporting.";
        return;
      }

      const agents = Array.isArray(data.agents) ? data.agents : [];
      const hasPatients = agents.some(
        (agent) => Array.isArray(agent.patients) && agent.patients.length > 0
      );
      if (!hasPatients) {
        this.errorMessage = "There is no data to export for the selected filters.";
        return;
      }

      this.loadingExcel = true;
      this.errorMessage = null;

      try {
        const wb = XLSX.utils.book_new();
        const sheets = [
          ["Agents", this.buildAgentsSheet(agents)],
          ["Totals", this.buildTotalsSheet(data.totals)],
          ["Visit Counts", this.buildVisitCountsSheet(data.visit_counts)],
          ["Visit Ref Counts", this.buildVisitReferenceCountsSheet(data.visit_reference_counts)],
          ["Patients Insurance", this.buildPatientsByInsuranceSheet(data.all_patients_by_insurance)],
        ];
        sheets.forEach(([title, [headers, rows]]) => {
          XLSX.utils.book_append_sheet(wb, this.createSheet(headers, rows), title);
        });

        const startDate = this.filters.start_date.replace(/-/g, "");
        const endDate = this.filters.end_date.replace(/-/g, "");
        XLSX.writeFile(wb, `dashboard-report-${startDate}-${endDate}.xlsx`);
      } catch (error) {
        this.errorMessage = "Error generating Excel file. Please try again.";
      } finally {
        this.loadingExcel = false;
      }
    },
    toCellValue(value) {
      if (value === null || value === undefined) return null;
      if (typeof value !== "string") return value;
      const text = value.trim();
      const isInteger = /^-?(0|[1-9]\d{0,14})$/.test(text);
      const isDecimal = /^-?(0|[1-9]\d{0,14})\.\d+$/.test(text);
      return isInteger || isDecimal ? Number(text) : value;
    },
    createSheet(headers, rows) {
      const aoa = [headers, ...rows.map((row) => row.map(this.toCellValue))];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = headers.map((header, colIndex) => {
        const width = aoa.reduce((max, row) => {
          const cell = row[colIndex];
          const length = cell === null || cell === undefined ? 0 : String(cell).length;
          return Math.max(max, length);
        }, 0);
        return { wch: Math.min(Math.max(width + 2, 10), 60) };
      });
      return ws;
    },
    buildAgentsSheet(agents = []) {
      const headers = [
        "agent_id", "agent_name", "agent_pay",
        "patient_id", "patient_name", "member_id", "patient_phone", "patient_email", "type_insurance",
        "visit_id", "visit_date", "type_visit", "facility", "total", "cash", "cc", "ajustment", "refund", "visit_status",
      ];
      const emptyVisit = Array(10).fill(null);
      const rows = [];

      agents.forEach((agent) => {
        const agentCols = [agent.agent_id, agent.agent_name, agent.agent_pay];
        const patients = Array.isArray(agent.patients) ? agent.patients : [];

        if (!patients.length) {
          rows.push([...agentCols, ...Array(6).fill(null), ...emptyVisit]);
          return;
        }

        patients.forEach((patient) => {
          const patientName = `${patient.name || ""} ${patient.lastname || ""}`.trim();
          const visits = Array.isArray(patient.visits) ? patient.visits : [];

          if (!visits.length) {
            rows.push([
              ...agentCols,
              patient.id, patientName, patient.member_id, patient.phone, patient.email, patient.type_insurance,
              ...emptyVisit,
            ]);
            return;
          }

          visits.forEach((visit) => {
            rows.push([
              ...agentCols,
              patient.id,
              visit.patient_name ?? patientName,
              patient.member_id,
              patient.phone,
              patient.email,
              visit.type_insurance ?? patient.type_insurance,
              visit.id,
              visit.visit_date,
              visit.type_visit,
              visit.facility,
              visit.total,
              visit.cash,
              visit.cc,
              visit.ajustment,
              visit.refund,
              visit.visit_status,
            ]);
          });
        });
      });

      return [headers, rows];
    },
    buildTotalsSheet(totals = {}) {
      return [["key", "value"], Object.entries(totals || {})];
    },
    buildVisitCountsSheet(visitCounts = []) {
      const rows = (Array.isArray(visitCounts) ? visitCounts : []).map((item) => [
        item.prefix,
        item.count ?? 0,
      ]);
      return [["prefix", "count"], rows];
    },
    buildVisitReferenceCountsSheet(data = {}) {
      const rows = [];
      Object.entries(data || {}).forEach(([typeVisit, items]) => {
        (Array.isArray(items) ? items : []).forEach((item) => {
          const text = item?.references || "";
          const separator = text.indexOf(":");
          const ref = separator >= 0 ? text.slice(0, separator).trim() : text.trim();
          const count = separator >= 0 ? text.slice(separator + 1).trim() : null;
          rows.push([typeVisit, ref, count]);
        });
      });
      return [["type_visit", "reference", "count"], rows];
    },
    buildPatientsByInsuranceSheet(groups = []) {
      const headers = [
        "type_insurance", "total_patients", "patient_id", "patient_name", "member_id",
        "phone", "email", "agent_name", "type_membership",
      ];
      const rows = [];

      (Array.isArray(groups) ? groups : []).forEach((group) => {
        const insurance = group.type_insurance ?? "Sin definir";
        const totalPatients = group.total_patients ?? 0;
        const patients = Array.isArray(group.patients) ? group.patients : [];

        if (!patients.length) {
          rows.push([insurance, totalPatients, ...Array(7).fill(null)]);
          return;
        }

        patients.forEach((patient) => {
          const agent = patient.agent_insurance || {};
          rows.push([
            insurance,
            totalPatients,
            patient.id,
            `${patient.name || ""} ${patient.lastname || ""}`.trim(),
            patient.member_id,
            patient.phone,
            patient.email,
            `${agent.name || ""} ${agent.lastname || ""}`.trim(),
            patient.type_membership,
          ]);
        });
      });

      return [headers, rows];
    },
    async fetchDashboardData() {
      if (!this.filters.start_date || !this.filters.end_date) {
        this.errorMessage = "Start and end dates are required.";
        return;
      }

      const payload = this.buildFiltersPayload();

      this.loading = true;
      this.errorMessage = null;

      try {
        const response = await adminAgentDataService.getDashboardData(payload);
        this.rawDashboardResponse = response;
        const dataBlock =
          response?.data ||
          response?.payload ||
          response?.resultado ||
          {};
        this.dashboardData = dataBlock;
        this.lastFetchedPayload = JSON.stringify(payload);
        
        // Detectar si debemos agrupar por agente (cuando no hay filtro de agente ni tipo de seguro)
        const shouldGroupByAgent = !this.filters.agent_user_id && !this.filters.type_insurance;
        this.isGroupedByAgent = shouldGroupByAgent && Array.isArray(dataBlock.agents) && dataBlock.agents.length > 0;
        
        if (this.isGroupedByAgent) {
          this.rawAgents = Array.isArray(dataBlock.agents) ? dataBlock.agents : [];
          this.rawPatients = [];
        } else {
          this.rawPatients = Array.isArray(dataBlock.patients)
            ? dataBlock.patients
            : [];
          this.rawAgents = [];
        }
        
        this.visitCounts = this.filterMembershipCounts(
          Array.isArray(dataBlock.visit_counts) ? dataBlock.visit_counts : []
        );
        this.totals = dataBlock.totals || {};
        this.patientsByInsurance = Array.isArray(
          dataBlock.all_patients_by_insurance
        )
          ? dataBlock.all_patients_by_insurance
          : [];

        this.refreshPatientRows();
        this.updateChartData();
        this.updatePatientsByInsuranceChart();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message ||
          "We could not fetch the dashboard information.";
        this.dashboardData = null;
        this.lastFetchedPayload = null;
        this.rawPatients = [];
        this.rawAgents = [];
        this.patientRows = [];
        this.agentRows = [];
        this.visitCounts = [];
        this.totals = {};
        this.patientsByInsurance = [];
        this.rawDashboardResponse = null;
        this.isGroupedByAgent = false;
        this.updateChartData();
        this.updatePatientsByInsuranceChart();
      } finally {
        this.loading = false;
      }
    },
    refreshPatientRows() {
      if (this.isGroupedByAgent) {
        this.agentRows = this.enrichAgents(this.rawAgents);
        this.patientRows = [];
      } else {
        this.patientRows = this.enrichPatients(this.rawPatients);
        this.agentRows = [];
      }
    },
    enrichAgents(agents = []) {
      return agents.map((agent) => {
        const agentPay = Number(agent.agent_pay) || 0;
        
        // Enriquecer pacientes pasando el pay_to_visit del agente
        const enrichedPatients = (Array.isArray(agent.patients) ? agent.patients : []).map((patient) => {
          const filteredVisits = (Array.isArray(patient.visits)
            ? patient.visits
            : []
          ).filter(
            (visit) =>
              this.isMembershipNP(visit.type_membership) ||
              this.isMembershipNC(visit.type_membership)
          );

          if (!filteredVisits.length) return null;

          // Obtener el nombre del agente
          const agentName = agent.agent_name || "Unknown Agent";

          // Enriquecer cada visita con el pay_to_visit del agente
          const enrichedVisits = filteredVisits.map((visit) => {
            // Buscar pay_to_visit en diferentes lugares
            const visitPayToVisit = Number(
              visit?.pay_to_visit ||
              visit?.agent_insurance?.pay_to_visit ||
              patient?.pay_to_visit ||
              patient?.agent_insurance?.pay_to_visit ||
              0
            );
            
            // Usar el pay_to_visit de la visita/patient si existe, sino usar agentPay
            const finalPayToVisit = visitPayToVisit > 0 ? visitPayToVisit : agentPay;
            
            // Valor por defecto cuando no se encuentra pay_to_visit
            const DEFAULT_PAY_TO_VISIT = 3;
            
            return {
              ...visit,
              // Usar pay_to_visit si existe, sino usar valor por defecto (3) en lugar del total del backend
              total: finalPayToVisit > 0 ? finalPayToVisit : DEFAULT_PAY_TO_VISIT,
              pay_to_visit: finalPayToVisit,
            };
          });

          return {
            ...patient,
            fullName: [patient.name, patient.lastname].filter(Boolean).join(" "),
            agentName: agentName,
            typeInsuranceLabel:
              this.getInsuranceNameById(patient.type_insurance) ||
              patient.type_insurance ||
              "Undefined",
            visits: enrichedVisits,
            visits_count: enrichedVisits.length,
            total_visit_amount: this.calculatePatientTotal(
              enrichedVisits,
              agentPay
            ),
          };
        }).filter(Boolean);
        
        const totalVisits = enrichedPatients.reduce(
          (sum, patient) => sum + (patient.visits_count || 0),
          0
        );
        
        // Calcular el total usando pay_to_visit × número de visitas
        const totalAmount = agentPay > 0 
          ? totalVisits * agentPay
          : enrichedPatients.reduce(
              (sum, patient) => sum + (patient.total_visit_amount || 0),
              0
            );
        
        return {
          agent_id: agent.agent_id,
          agent_name: agent.agent_name || "Unknown Agent",
          agent_pay: agentPay,
          patients: enrichedPatients,
          patients_count: enrichedPatients.length,
          total_visits: totalVisits,
          total_amount: totalAmount,
        };
      });
    },
    enrichPatients(patients = []) {
      return patients
        .map((patient) => {
          const filteredVisits = (Array.isArray(patient.visits)
            ? patient.visits
            : []
          ).filter(
            (visit) =>
              this.isMembershipNP(visit.type_membership) ||
              this.isMembershipNC(visit.type_membership)
          );

          if (!filteredVisits.length) return null;

          // Obtener el pay_to_visit del agente desde agent_insurance
          const agentPayToVisit = Number(
            patient?.agent_insurance?.pay_to_visit ||
            patient?.pay_to_visit ||
            0
          );

          // Enriquecer cada visita con el pay_to_visit del agente
          const enrichedVisits = filteredVisits.map((visit) => {
            // Buscar pay_to_visit en la visita misma primero
            const visitPayToVisit = Number(
              visit?.pay_to_visit ||
              visit?.agent_insurance?.pay_to_visit ||
              0
            );
            
            // Usar el pay_to_visit de la visita si existe, sino usar agentPayToVisit del patient
            const finalPayToVisit = visitPayToVisit > 0 ? visitPayToVisit : agentPayToVisit;
            
            // Valor por defecto cuando no se encuentra pay_to_visit
            const DEFAULT_PAY_TO_VISIT = 3;
            
            return {
              ...visit,
              // Usar pay_to_visit si existe, sino usar valor por defecto (3) en lugar del total del backend
              total: finalPayToVisit > 0 ? finalPayToVisit : DEFAULT_PAY_TO_VISIT,
              pay_to_visit: finalPayToVisit,
            };
          });

          // Obtener el nombre del agente
          const agentName = patient?.agent_insurance
            ? [patient.agent_insurance.name, patient.agent_insurance.lastname]
                .filter(Boolean)
                .join(" ") || "Unknown Agent"
            : "Unknown Agent";

          return {
            ...patient,
            fullName: [patient.name, patient.lastname].filter(Boolean).join(" "),
            agentName: agentName,
            typeInsuranceLabel:
              this.getInsuranceNameById(patient.type_insurance) ||
              patient.type_insurance ||
              "Undefined",
            visits: enrichedVisits,
            visits_count: enrichedVisits.length,
            total_visit_amount: this.calculatePatientTotal(
              enrichedVisits,
              agentPayToVisit
            ),
          };
        })
        .filter(Boolean);
    },
    calculatePatientTotal(visits = [], payToVisit = 0) {
      // Si payToVisit está disponible, usar: número de visitas × pay_to_visit
      // Si no, usar el total de cada visita (comportamiento anterior)
      if (payToVisit > 0) {
        return visits.length * payToVisit;
      }
      // Fallback: sumar el total de cada visita si viene en la respuesta
      return visits.reduce(
        (acc, visit) => acc + (Number(visit.total) || 0),
        0
      );
    },
    getInsuranceNameById(id) {
      if (id === null || id === undefined) return null;
      return this.insuranceTypeMap[id] || null;
    },
    updateChartData() {
      const categories = this.visitCounts.map((item) => item.prefix);
      const data = this.visitCounts.map((item) => Number(item.count || 0));
      this.visitCountChartOptions = {
        ...this.visitCountChartOptions,
        xaxis: {
          ...this.visitCountChartOptions.xaxis,
          categories,
        },
      };
      this.visitCountChartSeries = [
        {
          name: "Visits",
          data,
        },
      ];
    },
    updatePatientsByInsuranceChart() {
      const grouped = (this.patientsByInsurance || [])
        .map((item) => {
          const patients = (Array.isArray(item.patients) ? item.patients : []).filter(
            (patient) =>
              this.isMembershipNP(patient.type_membership) ||
              this.isMembershipNC(patient.type_membership)
          );

          if (!patients.length) return null;

          return {
            name: item.type_insurance || "Undefined",
            np: patients.filter((patient) =>
              this.isMembershipNP(patient.type_membership)
            ).length,
            nc: patients.filter((patient) =>
              this.isMembershipNC(patient.type_membership)
            ).length,
          };
        })
        .filter(Boolean);

      if (!grouped.length) {
        this.patientsByInsuranceChartOptions = {
          ...this.patientsByInsuranceChartOptions,
          xaxis: {
            ...this.patientsByInsuranceChartOptions.xaxis,
            categories: [],
          },
        };
        this.patientsByInsuranceChartSeries = [
          { name: "New patients (NP)", data: [] },
          { name: "Returning patients (NC)", data: [] },
        ];
        return;
      }

      const categories = grouped.map((item) => item.name);
      const npData = grouped.map((item) => item.np);
      const ncData = grouped.map((item) => item.nc);

      this.patientsByInsuranceChartOptions = {
        ...this.patientsByInsuranceChartOptions,
        xaxis: {
          ...this.patientsByInsuranceChartOptions.xaxis,
          categories,
        },
      };

      this.patientsByInsuranceChartSeries = [
        { name: "New patients (NP)", data: npData },
        { name: "Returning patients (NC)", data: ncData },
      ];
    },
    normalizeMembership(value) {
      return (value || "").replace(/[^a-zA-Z]/g, "").toUpperCase();
    },
    isMembershipNP(value) {
      const normalized = this.normalizeMembership(value);
      return normalized === "NP" || normalized.endsWith("NP");
    },
    isMembershipNC(value) {
      const normalized = this.normalizeMembership(value);
      return normalized === "NC" || normalized.endsWith("NC");
    },
    filterMembershipCounts(counts = []) {
      return counts.filter((item) => {
        const normalized = this.normalizeMembership(item?.prefix);
        return (
          normalized === "NP" ||
          normalized === "NC" ||
          normalized.endsWith("NP") ||
          normalized.endsWith("NC")
        );
      });
    },
    formatCurrency(value) {
      const numericValue = Number(value) || 0;
      return new Intl.NumberFormat("es-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(numericValue);
    },
    formatNumber(value, isInteger = false) {
      const numericValue = Number(value) || 0;
      return new Intl.NumberFormat("es-US", {
        minimumFractionDigits: isInteger ? 0 : 2,
        maximumFractionDigits: isInteger ? 0 : 2,
      }).format(numericValue);
    },
  },
  watch: {
    async "filters.admin_user_id"(newValue, oldValue) {
      if (newValue === oldValue) return;
      await this.loadAgentOptions(newValue);
      if (this.shouldAutoFetch) {
        this.fetchDashboardData();
      }
    },
    "filters.agent_user_id"(newValue, oldValue) {
      if (this.isSettingAgentFilter) return;
      if (this.shouldAutoFetch && newValue !== oldValue) {
        this.fetchDashboardData();
      }
    },
    "filters.type_insurance"(newValue, oldValue) {
      if (this.shouldAutoFetch && newValue !== oldValue) {
        this.fetchDashboardData();
      }
    },
  },
  mounted() {
    Promise.all([
      this.loadAdminOptions(),
      this.loadAllAgents(),
      this.loadInsuranceTypes(),
    ]).finally(
      async () => {
        this.shouldAutoFetch = true;
      }
    );
  },
};
</script>

<style scoped>
.insurance-admin-agent-dashboard {
  background-color: #f4f6f8;
  min-height: 100vh;
}

.insurance-admin-agent-dashboard h2 {
  font-weight: 700;
}

.summary-card {
  background-color: #ffffff;
  border: 1px solid #e4e9f2;
}

.summary-card .h4 {
  font-weight: 700;
}

.gap-2 {
  gap: 0.5rem;
}

.agent-data-table ::v-deep th {
  text-transform: capitalize;
}

.visit-totals-legend-item:last-child {
  margin-bottom: 0 !important;
}

.visit-totals-legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
}

@media (max-width: 767px) {
  .visit-totals-legend-item {
    justify-content: flex-start;
  }
}
</style>

